import { DayHour, DayNumber, MapLike, MinuteNumber, MonthNumber, ValidBusinessObject } from '@iotv/iotv-v3-types'
import { Milliseconds } from '../../../../constants'
import { HourlyYearBasedArrayDef, HourlyYearBasedArrayDefType } from '../sharedTypes'
import { DimensionedPeriodizer, DimensionedPeriodizerProps } from './DimensionedPeriodizer'
import {
  DEBUG_OVERRIDE,
  NOT_LAMBDA,
  TimeBucketType,
  TimeBucketUnits,
  daysInLastUTCYear,
  monthDaysDiff,
  comparableProductOf,
  daysInThisUTCMonth,
} from '../../../../util'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

const className = 'HourlyYearPeriodizer'

export type HourlyYearPeriodizerProps = DimensionedPeriodizerProps & {
  arrayLength?: HourlyYearBasedArrayDefType
  timeBucketDefiniion?: TimeBucketType & {
    unit: typeof TimeBucketUnits.DAY
    width: 1
  }
}

/**
 * @class QuarterHourPeriodizer manages periodization for 1 month of quarter hour peridos
 */
export class HourlyYearPeriodizer extends DimensionedPeriodizer<MapLike<number | null>, HourlyYearBasedArrayDefType> {
  constructor(ob?: HourlyYearPeriodizerProps & ValidBusinessObject) {
    super(ob)
    if (!ob) {
      this.type = className
      this.arrayLengths = HourlyYearBasedArrayDef
      this.initialize()
    }
  }

  timeBucketDefinition: TimeBucketType = {
    unit: TimeBucketUnits.HOUR,
    width: 1,
  }

  timeBucketToAccessor = (timeBucketAccessor: [number, MonthNumber, DayNumber, DayHour, MinuteNumber, MinuteNumber]) => {
    const [y, m, d, h, min, s] = timeBucketAccessor
    const thisAccessor: [MonthNumber, DayNumber, DayHour] = [m, (d - 1) as DayNumber, h]
    return thisAccessor
  }

  periodizerDaysLength = () => daysInLastUTCYear(this.lastUpdatedTimestamp ?? 0)

  getFirstPossibleTimestamp = () => (this.lastUpdatedTimestamp ?? 0) - this.periodizerDaysLength() * Milliseconds.DAY + 1 * Milliseconds.HOUR

  accessorToTimestamp = (accessor: number[]): number => {
    const startTimestamp = this.lastUpdatedTimestamp ?? 0
    const currentAccessor = this.timestampToAccessor(startTimestamp)
    const [_monthsDiff, daysDiff, hoursDiff] = accessor.map((v, i) => v - currentAccessor[i])

    // handle rollover by when days same hour same quarter more, days same hour more, days more
    let absoluteNegativeDiff: number | undefined
    const currentMonth = currentAccessor[0]
    const acccessorMonth = accessor[0]
    const monthDaysDiffRes = monthDaysDiff(acccessorMonth, currentMonth, this.lastUpdatedTimestamp ?? 0)
    const relativeDiff = monthDaysDiffRes * Milliseconds.DAY + daysDiff * Milliseconds.DAY + hoursDiff * Milliseconds.HOUR

    if (relativeDiff > 0) {
      // absolute diff A + B is length of bucket minus how far ahead the accessor is xxxxx  [<---A---xxxxx--B-->]
      absoluteNegativeDiff = -(this.periodizerDaysLength() * Milliseconds.DAY - relativeDiff)
    } else {
      absoluteNegativeDiff = relativeDiff
    }

    const impliedTimestamp = startTimestamp + absoluteNegativeDiff

    // if ( accessor.every( ( v, i) => v === 0 ) ) { console.log({ accessor, currentAccessor, _monthsDiff, daysDiff, hoursDiff, monthDaysDiffRes, relativeDiff, impliedTimestamp, date: new Date(impliedTimestamp).toUTCString() }) }

    return impliedTimestamp
  }

  isValidAccessor = (args: number[]): args is HourlyYearBasedArrayDefType => {
    const date = new Date(this.lastUpdatedTimestamp ?? 0)
    const currentAccessor = this.timestampToAccessor(this.lastUpdatedTimestamp ?? 0)
    if (this.arrayLengths && comparableProductOf(args, this.arrayLengths) > comparableProductOf(currentAccessor, this.arrayLengths)) {
      // in year before, still possible that 31 30 may cause an issue
      date.setUTCFullYear(date.getUTCFullYear() - 1, args[0], 1)
    } else {
      date.setUTCMonth(args[0], 1)
    }
    const maxDays = daysInThisUTCMonth(date.valueOf())

    const isValid = args.length === this.arrayLengths?.length && args.every((d, i) => d >= 0 && d < (this.arrayLengths?.[i] ?? 0)) && args[1] <= maxDays - 1
    if (!isValid) {
      debug && console.log(`Not valid accessor`, args)
    }
    debug && console.log({ accessor: args, maxDays, isValid, date: date.toUTCString() })
    return isValid
  }
}
